import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import RecurrenceFields from "../components/RecurrenceFields";
import { getMyTemplateSettings, updateMyTemplateSettings } from "../api/users";
import {
  formatTaskSchedule,
  normalizeScheduleSetting,
  validateScheduleSetting,
} from "../utils/taskSchedule";

/** AppShell の永続ドロワー幅（`AppShell.js` の DRAWER_WIDTH と一致） */
const MAIN_DRAWER_WIDTH_PX = 280;

function normalizeTemplateSettings(settings = []) {
  return settings.map((setting) => normalizeScheduleSetting(setting));
}

function TemplateSettings() {
  const [settings, setSettings] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getMyTemplateSettings();
        if (!cancelled) {
          setSettings(normalizeTemplateSettings(response));
        }
      } catch (error) {
        if (!cancelled) {
          setFeedback({
            open: true,
            message: error.message || "テンプレート利用設定の取得に失敗しました。",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = (templateId) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.template_id === templateId
          ? { ...setting, is_enabled: !setting.is_enabled }
          : setting
      )
    );
  };

  const handleScheduleChange = (templateId, updates) => {
    setSettings((prev) =>
      prev.map((setting) =>
        setting.template_id === templateId
          ? normalizeScheduleSetting({ ...setting, ...updates })
          : setting
      )
    );
    setErrors((prev) => {
      if (!prev[templateId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  };

  const handleSave = async () => {
    const normalizedSettings = settings.map((setting) => normalizeScheduleSetting(setting));
    const nextErrors = {};
    normalizedSettings.forEach((setting) => {
      const error = validateScheduleSetting(setting);
      if (error) {
        nextErrors[setting.template_id] = error;
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFeedback({
        open: true,
        message: "繰り返し設定に未入力があります。",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await updateMyTemplateSettings(
        normalizedSettings.map((setting) => ({
          template_id: setting.template_id,
          is_enabled: setting.is_enabled,
          recurrence_type: setting.recurrence_type,
          weekly_days: setting.weekly_days,
          monthly_days: setting.monthly_days,
        }))
      );
      setSettings(normalizeTemplateSettings(updated));
      setErrors({});
      setFeedback({
        open: true,
        message: "自動生成のテンプレート設定を保存しました。",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error.message || "テンプレート利用設定の保存に失敗しました。",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Stack spacing={2} sx={{ pb: 12 }}>
      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={1}>
          <Typography variant="h6">自動生成に使うテンプレート</Typography>
          <Typography variant="body2" color="text.secondary">
            ON にしたテンプレートだけが自動生成対象になり、曜日や日付の条件もここで設定できます。
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {settings.length === 0 ? (
          <Alert severity="info">利用可能なテンプレートがありません。</Alert>
        ) : (
          <Stack spacing={2}>
            {settings.map((setting) => {
              const scheduleLabel = formatTaskSchedule(
                setting.recurrence_type,
                setting.weekly_days,
                setting.monthly_days
              );

              return (
              <Box
                key={setting.template_id}
                sx={{
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{setting.template_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {setting.is_enabled ? "自動生成ON" : "自動生成OFF"} / 設定中: {scheduleLabel || "毎日"}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(setting.is_enabled)}
                          onChange={() => handleToggle(setting.template_id)}
                        />
                      }
                      label={setting.is_enabled ? "使用する" : "使用しない"}
                      sx={{ mr: 0 }}
                    />
                  </Box>
                  <Stack spacing={1} sx={{ maxWidth: 420 }}>
                    <RecurrenceFields
                      idPrefix={`template-setting-${setting.template_id}`}
                      recurrenceType={setting.recurrence_type}
                      weeklyDays={setting.weekly_days}
                      monthlyDays={setting.monthly_days}
                      onRecurrenceTypeChange={(value) =>
                        handleScheduleChange(setting.template_id, { recurrence_type: value })
                      }
                      onWeeklyDaysChange={(value) =>
                        handleScheduleChange(setting.template_id, { weekly_days: value })
                      }
                      onMonthlyDaysChange={(value) =>
                        handleScheduleChange(setting.template_id, { monthly_days: value })
                      }
                    />
                    {errors[setting.template_id] && (
                      <Alert severity="error">{errors[setting.template_id]}</Alert>
                    )}
                  </Stack>
                </Stack>
              </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
      </Stack>

      <Paper
        component="footer"
        elevation={0}
        square
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: { xs: 0, lg: `${MAIN_DRAWER_WIDTH_PX}px` },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: { xs: 2, sm: 3, lg: 4 },
          py: 1.5,
          pb: {
            xs: "calc(12px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(12px + env(safe-area-inset-bottom, 0px))",
          },
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(14px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            maxWidth: { lg: 1200 },
            mx: { lg: "auto" },
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 180 } }}
          >
            {saving ? "保存中..." : "設定を保存する"}
          </Button>
        </Box>
      </Paper>

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

export default TemplateSettings;
